<template>
  <b-sidebar
    id="request-specialist-sidebar"
    sidebar-class="sidebar-lg"
    :visible="visible"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @change="$emit('change', $event)"
    @hidden="handleHidden"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <div>
          <h4 class="mb-0">Atendimento prioritário</h4>
          <small class="text-muted">
            {{ customerName }}
          </small>
        </div>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <div class="p-2">
        <div
          v-if="loadingHistory"
          class="d-flex align-items-center text-primary my-3"
        >
          <b-spinner small class="mr-50" />
          <span>Carregando solicitações...</span>
        </div>

        <template v-else>
          <div class="mb-2">
            Solicitações registradas: <strong>{{ requests.length }}</strong>
          </div>

          <div
            v-if="allowCreate"
            class="mb-3"
          >
            <h5 class="mb-1">Nova solicitação</h5>
            <b-form-group
              label="Unidade de Negócio"
              label-for="requestSpecialistDepartment"
            >
              <v-select
                id="requestSpecialistDepartment"
                v-model="createForm.department_id"
                :reduce="department => department.id"
                :options="businessUnits"
                label="name"
                :clearable="false"
                dir="ltr"
                @input="handleCreateDepartmentChange"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>

            <b-form-group
              label="Especialista"
              label-for="requestSpecialistConsultant"
            >
              <v-select
                id="requestSpecialistConsultant"
                v-model="createForm.specialist_consultant_id"
                :reduce="consultant => consultant.id"
                :options="createConsultants"
                label="name"
                dir="ltr"
              >
                <template #no-options>
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>

            <b-form-group
              label="Observações"
              label-for="requestSpecialistNotes"
            >
              <b-form-textarea
                id="requestSpecialistNotes"
                v-model="createForm.notes"
                rows="3"
                max-rows="5"
              />
            </b-form-group>

            <div class="d-flex">
              <b-button
                variant="primary"
                :disabled="savingCreate || !createForm.department_id"
                @click="createRequest"
              >
                <b-spinner
                  v-if="savingCreate"
                  small
                />
                <span>{{ savingCreate ? 'Aguarde...' : 'Solicitar' }}</span>
              </b-button>
            </div>
          </div>

          <div>
            <h5 class="mb-1">Histórico</h5>

            <p
              v-if="!requests.length"
              class="text-muted mb-0"
            >
              Nenhuma solicitação registrada para este cliente.
            </p>

            <div
              v-for="requestItem in requests"
              :key="requestItem.id"
              class="request-card border rounded p-1 mb-2"
            >
              <div class="d-flex justify-content-between align-items-start flex-wrap">
                <div class="mr-1">
                  <div class="font-weight-bolder">
                    {{ requestItem.department_name || '-' }}
                  </div>
                  <div class="text-muted small">
                    Solicitado em {{ formatDateTime(requestItem.requested_at) }}
                  </div>
                </div>

                <b-badge :variant="statusVariant(requestItem.status)">
                  {{ statusLabel(requestItem.status) }}
                </b-badge>
              </div>

              <div class="mt-1">
                <div class="d-flex align-items-center flex-wrap mb-25">
                  <span class="mr-50">
                    <span class="text-muted">Especialista:</span>
                    <span class="font-weight-bolder">
                      {{ requestItem.specialist_name || requestItem.specialist_label || '-' }}
                    </span>
                  </span>
                  <b-badge
                    v-if="requestItem.specialist_commission_level"
                    variant="light-primary"
                  >
                    {{ requestItem.specialist_commission_level }}
                  </b-badge>
                </div>

                <div class="mb-25">
                  <span class="text-muted">Atribuído:</span> {{ formatDateTime(requestItem.assigned_at) }}
                  <span v-if="requestItem.completed_at">
                    <span class="text-muted">· Concluído:</span> {{ formatDateTime(requestItem.completed_at) }}
                  </span>
                </div>
                <div class="mb-25">
                  <span class="text-muted">Solicitante:</span> {{ requestItem.requested_by_name || '-' }}
                </div>
                <div v-if="requestItem.notes">
                  <span class="text-muted">Observações:</span> {{ requestItem.notes }}
                </div>
              </div>

              <div
                v-if="isActive(requestItem.status) && allowCreate"
                class="border-top pt-1 mt-1"
              >
                <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
                  <span class="font-weight-bolder small">Ações</span>
                  <div class="d-flex flex-wrap">
                    <b-button
                      size="sm"
                      variant="outline-success"
                      class="mr-50 mb-50"
                      :disabled="statusLoadingId === requestItem.id"
                      @click="updateStatus(requestItem.id, 'completed')"
                    >
                      Concluir
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="mb-50"
                      :disabled="statusLoadingId === requestItem.id"
                      @click="updateStatus(requestItem.id, 'cancelled')"
                    >
                      Cancelar
                    </b-button>
                  </div>
                </div>

                <b-form-group
                  label="Reatribuir especialista"
                  :label-for="`requestSpecialistEdit-${requestItem.id}`"
                  class="mb-1"
                >
                  <v-select
                    :id="`requestSpecialistEdit-${requestItem.id}`"
                    v-model="editingRequests[requestItem.id].specialist_consultant_id"
                    :reduce="consultant => consultant.id"
                    :options="editingRequests[requestItem.id].consultants"
                    label="name"
                    dir="ltr"
                  >
                    <template #no-options>
                      Sem resultados
                    </template>
                  </v-select>
                </b-form-group>

                <b-form-group
                  label="Observações"
                  :label-for="`requestSpecialistEditNotes-${requestItem.id}`"
                >
                  <b-form-textarea
                    :id="`requestSpecialistEditNotes-${requestItem.id}`"
                    v-model="editingRequests[requestItem.id].notes"
                    rows="2"
                    max-rows="4"
                  />
                </b-form-group>

                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="
                    specialistLoadingId === requestItem.id
                    || !editingRequests[requestItem.id].specialist_consultant_id
                  "
                  @click="saveSpecialist(requestItem.id)"
                >
                  <b-spinner
                    v-if="specialistLoadingId === requestItem.id"
                    small
                  />
                  <span>{{ specialistLoadingId === requestItem.id ? 'Aguarde...' : 'Salvar especialista' }}</span>
                </b-button>
              </div>

              <div class="border-top pt-1 mt-1">
                <div class="font-weight-bolder small mb-1">Movimentações</div>

                <p
                  v-if="!requestItem.movements || !requestItem.movements.length"
                  class="text-muted small mb-0"
                >
                  Nenhuma movimentação registrada.
                </p>

                <div
                  v-for="movement in requestItem.movements"
                  :key="movement.id"
                  class="border-left pl-1 mb-1"
                >
                  <div class="d-flex justify-content-between align-items-center flex-wrap">
                    <span class="font-weight-bolder small">{{ movementTypeLabel(movement) }}</span>
                    <small class="text-muted">{{ formatDateTime(movement.created_at) }}</small>
                  </div>
                  <div class="small">
                    <span class="text-muted">{{ movement.performed_by_name || '-' }}</span>
                    <span v-if="movement.from_status || movement.to_status">
                      · {{ movement.from_status ? statusLabel(movement.from_status) + ' → ' : '' }}{{ statusLabel(movement.to_status) }}
                    </span>
                  </div>
                  <div
                    v-if="movement.previous_specialist_label || movement.specialist_label"
                    class="small"
                  >
                    <span v-if="movement.previous_specialist_label">{{ movement.previous_specialist_label }} → </span>{{ movement.specialist_label }}
                  </div>
                  <div
                    v-if="movement.notes"
                    class="small"
                  >
                    <span class="text-muted">Obs:</span> {{ movement.notes }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BBadge,
  BButton,
  BFormGroup,
  BFormTextarea,
  BSidebar,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as customerTypes from '@/modules/customer/store/types'
import {
  getRequestSpecialistStatusLabel,
  getRequestSpecialistStatusVariant,
  isRequestSpecialistActive,
} from '@/constants/requestSpecialist'

export default {
  components: {
    BBadge,
    BButton,
    BFormGroup,
    BFormTextarea,
    BSidebar,
    BSpinner,
    vSelect,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    customer: {
      type: Object,
      default: () => null,
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    return { toast: useToast() }
  },
  data() {
    return {
      loadingHistory: false,
      savingCreate: false,
      specialistLoadingId: null,
      statusLoadingId: null,
      businessUnits: [],
      requests: [],
      createConsultants: [],
      createForm: {
        department_id: null,
        specialist_consultant_id: null,
        notes: '',
      },
      editingRequests: {},
      consultantsByDepartment: {},
    }
  },
  computed: {
    customerId() {
      return this.customer?.id || this.customer?.customer_id || null
    },
    customerName() {
      return this.customer?.customer_name || this.customer?.name || '-'
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.initialize()
      }
    },
    customerId() {
      if (this.visible) {
        this.initialize()
      }
    },
  },
  methods: {
    ...mapActions({
      fetchBusinessUnits: customerTypes.REQUEST_SPECIALIST_BUSINESS_UNITS,
      fetchHistory: customerTypes.GET_REQUEST_SPECIALIST_HISTORY,
      fetchRequestSpecialistConsultants: customerTypes.REQUEST_SPECIALIST_CONSULTANTS,
      assignRequestSpecialist: customerTypes.ASSIGN_REQUEST_SPECIALIST,
      updateRequestSpecialist: customerTypes.UPDATE_REQUEST_SPECIALIST,
      updateRequestSpecialistStatus: customerTypes.UPDATE_REQUEST_SPECIALIST_STATUS,
    }),
    async initialize() {
      if (!this.customerId) {
        return
      }

      await Promise.all([
        this.loadBusinessUnits(),
        this.loadHistory(),
      ])
    },
    async loadBusinessUnits() {
      if (this.businessUnits.length) {
        return
      }

      const response = await this.fetchBusinessUnits()
      this.businessUnits = response.data || []
    },
    async loadHistory() {
      this.loadingHistory = true

      try {
        const response = await this.fetchHistory({ customerId: this.customerId })
        this.requests = response.data?.requests || []
        await this.prepareEditingState()
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: 'Ocorreu um erro ao carregar o histórico de atendimento prioritário.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loadingHistory = false
      }
    },
    async prepareEditingState() {
      const nextState = {}

      for (const requestItem of this.requests) {
        const consultants = await this.loadConsultants(requestItem.department_id)
        nextState[requestItem.id] = {
          specialist_consultant_id: requestItem.specialist_consultant_id || null,
          notes: requestItem.notes || '',
          consultants,
        }
      }

      this.editingRequests = nextState
    },
    async loadConsultants(departmentId) {
      if (!departmentId) {
        return []
      }

      if (this.consultantsByDepartment[departmentId]) {
        return this.consultantsByDepartment[departmentId]
      }

      const response = await this.fetchRequestSpecialistConsultants({ department_id: departmentId })

      this.consultantsByDepartment = {
        ...this.consultantsByDepartment,
        [departmentId]: response.data || [],
      }

      return this.consultantsByDepartment[departmentId]
    },
    async handleCreateDepartmentChange(departmentId) {
      this.createForm.specialist_consultant_id = null
      this.createConsultants = await this.loadConsultants(departmentId)

      if (this.createConsultants.length === 1) {
        this.createForm.specialist_consultant_id = this.createConsultants[0].id
      }
    },
    async createRequest() {
      this.savingCreate = true

      try {
        await this.assignRequestSpecialist({
          customer_ids: [this.customerId],
          department_id: this.createForm.department_id,
          specialist_consultant_id: this.createForm.specialist_consultant_id,
          notes: this.createForm.notes || null,
        })

        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso!',
            text: 'Solicitação registrada com sucesso.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.resetCreateForm()
        await this.loadHistory()
        this.$emit('refresh')
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: error?.response?.data?.message
              || 'Não foi possível registrar a solicitação.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.savingCreate = false
      }
    },
    async saveSpecialist(requestId) {
      const editingItem = this.editingRequests[requestId]
      if (!editingItem?.specialist_consultant_id) {
        return
      }

      this.specialistLoadingId = requestId

      try {
        await this.updateRequestSpecialist({
          requestId,
          params: {
            specialist_consultant_id: editingItem.specialist_consultant_id,
            notes: editingItem.notes || null,
          },
        })

        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso!',
            text: 'Especialista atualizado com sucesso.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        await this.loadHistory()
        this.$emit('refresh')
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: error?.response?.data?.message
              || 'Não foi possível atualizar o especialista.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.specialistLoadingId = null
      }
    },
    async updateStatus(requestId, status) {
      this.statusLoadingId = requestId

      try {
        await this.updateRequestSpecialistStatus({
          requestId,
          params: {
            status,
            notes: this.editingRequests[requestId]?.notes || null,
          },
        })

        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso!',
            text: 'Status atualizado com sucesso.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        await this.loadHistory()
        this.$emit('refresh')
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: error?.response?.data?.message
              || 'Não foi possível atualizar o status.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.statusLoadingId = null
      }
    },
    resetCreateForm() {
      this.createForm = {
        department_id: null,
        specialist_consultant_id: null,
        notes: '',
      }
      this.createConsultants = []
    },
    handleHidden() {
      this.resetCreateForm()
      this.$emit('hidden')
    },
    statusLabel(status) {
      return getRequestSpecialistStatusLabel(status)
    },
    statusVariant(status) {
      return getRequestSpecialistStatusVariant(status)
    },
    isActive(status) {
      return isRequestSpecialistActive(status)
    },
    movementTypeLabel(movement) {
      switch (movement.movement_type) {
        case 'requested':
          return 'Solicitação criada'
        case 'assigned':
          return 'Especialista atribuído'
        case 'reassigned':
          return 'Especialista reatribuído'
        case 'status_changed':
          return 'Status alterado'
        default:
          return 'Movimentação'
      }
    },
    formatDateTime(value) {
      if (!value) {
        return '-'
      }

      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    },
  },
}
</script>
